import { memo, useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { isUploadedVideo } from '@goldcast/player-config/src/utils/mediaContentUtils';
import { assetsMediaContentRetrieve } from '@goldcast/api/content';
import { getSessionPreviewState } from '../utils';
import SessionPreviewHeader from './SessionPreviewHeader';
import SessionPreviewFooter from './SessionPreviewFooter';
import type { SessionPreviewProps } from './types';
import { classnames } from '@/libs/utils';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { isSimplifiedUploadEnabled } from '@/stores/clip';
import featureFlagStore from '@/stores/featureFlagStore';
import { READINESS_SHOW_STATUS_EVENT } from '@/components/organisms/ContentReadinessPopup/constants';
import { showErrorToast } from '@/libs/toast/toast';

function SessionPreview({ session }: SessionPreviewProps) {
  const navigate = useNavigate();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isCLHomePageEnabled = Boolean(featureFlags[FeatureFlagKeys.Use_CL_New_HomePage]);

  const {
    projectId,
    sessionId,
    isContentUploadInProgress,
    contentStatusText,
    canPreviewSession,
    canOpenSession,
    isRecordingEventProcessing,
    isVideoReadyForPreview,
    isDisabled,
    isBatchFailed,
    failedIconName,
    endTime
  } = useMemo(() => getSessionPreviewState(session), [session]);

  // Prevents double-firing if the user clicks while an in-flight status check is pending.
  const isCheckingStatus = useRef(false);

  const handleContentClick = useCallback(async () => {
    if (isRecordingEventProcessing) {
      window.location.replace(`/recording-event/${projectId}`);
      return;
    }

    if (!canOpenSession) {
      return;
    }

    // Simplified upload flow: always fetch the live upload_editor_status rather than relying on
    // the stale list value. The list cache can have null/undefined for an upload that was in
    // flight when the list loaded, which would fall through to the legacy /import/ route and
    // break the simplified-upload experience. Mirrors the VideoLibraryTable click→API gate.
    if (isSimplifiedUploadEnabled() && isUploadedVideo(session.media_source_type)) {
      if (isCheckingStatus.current) {
        return;
      }
      isCheckingStatus.current = true;
      try {
        const media = await assetsMediaContentRetrieve({ id: sessionId });
        if (media.upload_editor_status === 'DONE') {
          navigate(`/${projectId}/${sessionId}/recordings`);
        } else if (media.upload_editor_status) {
          // PROCESSING or FAILED — surface readiness popup, do not open editor.
          document.dispatchEvent(
            new CustomEvent(READINESS_SHOW_STATUS_EVENT, {
              detail: {
                content_id: sessionId,
                project_id: projectId,
                title: media.title,
                upload_editor_status: media.upload_editor_status,
                batch_status: media.batch_status ?? null
              }
            })
          );
        } else {
          navigate(
            isContentUploadInProgress ? `/import/${projectId}/${sessionId}` : `/${projectId}/${sessionId}/recordings`
          );
        }
      } catch {
        showErrorToast('Something went wrong while opening this video. Please try again.');
      } finally {
        isCheckingStatus.current = false;
      }
      return;
    }

    navigate(isContentUploadInProgress ? `/import/${projectId}/${sessionId}` : `/${projectId}/${sessionId}/recordings`);
  }, [
    canOpenSession,
    isContentUploadInProgress,
    isRecordingEventProcessing,
    navigate,
    projectId,
    session.media_source_type,
    sessionId
  ]);

  return (
    <button
      className={classnames(
        'flex w-[23rem] shrink-0 flex-col divide-y divide-slate-200/80 truncate !rounded-xl border !border-slate-200/80',
        {
          'cursor-pointer transition-all duration-[250ms] ease-in-out hover:scale-[1.0075] hover:shadow-lg hover:shadow-slate-200':
            isVideoReadyForPreview,
          'cursor-not-allowed': isDisabled,
          'h-[235px] w-[298px] hover:!shadow-md': isCLHomePageEnabled
        }
      )}
      onClick={handleContentClick}
      data-testid="session-preview"
    >
      <SessionPreviewHeader
        session={session}
        canPreviewSession={canPreviewSession}
        isBatchFailed={isBatchFailed}
        failedIconName={failedIconName}
        contentStatusText={contentStatusText}
      />
      <SessionPreviewFooter
        title={session.title}
        endTime={endTime}
        isCLHomePageEnabled={isCLHomePageEnabled}
        isVideoReadyForPreview={isVideoReadyForPreview}
        contentStatusText={contentStatusText}
      />
    </button>
  );
}

export default memo(SessionPreview);
